import React from "react";
import { Satellite, ShieldCheck, BookOpen, PackageOpen, CheckCircle2, XCircle, Zap } from "lucide-react";

// Single source of truth for task-verification status. Shows whether WikiSync
// (quests/diaries) and TempleOSRS (collection log) are connected, live stats when
// available, setup instructions when not, and one Sync button.
export default function SyncOnboarding({ profile, onSync, syncing }) {
  const ws = profile.external?.wikisync;
  const temple = profile.external?.temple;
  const wom = profile.external?.wom;
  const wsOk = !!ws?.synced;
  const templeOk = !!temple?.synced;
  const allOk = wsOk && templeOk;

  const Row = ({ ok, icon: Icon, title, testid, connected, instructions }) => (
    <div className="flex items-start gap-3" data-testid={`verif-row-${testid}`}>
      <div className="skill-node p-2 shrink-0">
        <Icon size={18} className={ok ? "text-[#00FF00]" : "text-[#FFCF3F]"} />
      </div>
      <div className="min-w-0">
        <p className="osrs-shadow text-[#FFCF3F] font-bold text-sm flex items-center gap-2 flex-wrap">
          {title}
          {ok ? (
            <span className="inline-flex items-center gap-1 text-[#00FF00] text-xs" data-testid={`verif-badge-${testid}`}>
              <CheckCircle2 size={12} /> connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[#FF7A7A] text-xs" data-testid={`verif-badge-${testid}`}>
              <XCircle size={12} /> not synced
            </span>
          )}
        </p>
        <p className="text-[#E6A519] text-xs leading-snug mt-0.5">{ok ? connected : instructions}</p>
      </div>
    </div>
  );

  return (
    <div className={`osrs-panel p-4 mb-6 ${allOk ? "" : "border-2 border-[#8a6d1f]"}`} data-testid="sync-onboarding">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#FFCF3F]" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
          <h3 className="osrs-title text-lg leading-tight">Task Verification</h3>
        </div>
        <button
          data-testid="onboard-sync-btn"
          onClick={onSync}
          disabled={syncing}
          className="osrs-btn osrs-btn-green px-4 py-2 text-sm flex items-center gap-2"
        >
          <Satellite size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync now"}
        </button>
      </div>

      <p className="osrs-shadow text-[#E6A519] text-xs mb-2 leading-snug">
        Every task is proven by trusted data — no self-reporting, so the monthly OSRS Bond race stays fair.
        {!allOk && " Connect these free RuneLite plugins, then hit Sync to unlock quest, diary & collection-log tasks."}
      </p>

      <p className="osrs-shadow text-[#7CFC7C] text-[11px] mb-4 leading-snug flex items-start gap-1.5" data-testid="wikisync-live-note">
        <Zap size={13} className="text-[#7CFC7C] shrink-0 mt-0.5" />
        <span><span className="text-[#FFCF3F]">Live skills:</span> the official Hiscores only update when you log out. With WikiSync connected, hit <span className="text-[#FFCF3F]">Sync now</span> to pull your latest levels in-game — no need to log out.</span>
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <Row
          ok={wsOk}
          icon={BookOpen}
          title="WikiSync — Quests & Diaries"
          testid="wikisync"
          connected="Quests, diaries & live skill levels are verifiable."
          instructions={<>Install the <span className="text-[#FFCF3F]">WikiSync</span> plugin in RuneLite and log into <span className="text-[#FFCF3F]">{profile.username}</span> in-game once. It then pushes level-ups & completions live (~10s) — just press Sync, no logout needed.</>}
        />
        <Row
          ok={templeOk}
          icon={PackageOpen}
          title="TempleOSRS — Collection Log"
          testid="templeosrs"
          connected={`Collection log connected — ${temple?.finished ?? 0}/${temple?.available ?? 0} slots logged.`}
          instructions={<>Install the <span className="text-[#FFCF3F]">TempleOSRS</span> plugin and open your collection log in-game to upload it, then press Sync.</>}
        />
      </div>

      {wom && (
        <p className="osrs-num text-xs text-[#E6A519] mt-3" data-testid="wom-status">
          Wise Old Man efficiency: <span className="text-[#FFCF3F]">{wom.ehp} EHP · {wom.ehb} EHB</span>
        </p>
      )}

      {!allOk && (
        <p className="osrs-shadow text-[11px] text-[#FF9966] mt-3 leading-snug" data-testid="bond-nudge">
          Unsynced accounts can't earn verified points — sync to compete for the monthly OSRS Bond.
        </p>
      )}
    </div>
  );
}
